import React, { useEffect, useState } from "react";
import axiosApi from "core/request";
import { useTranslation } from "react-i18next";
import { setAllRequestsFilter, setAllRequestsPage, setAllRequestsPageSize } from "store/slices/reguestsSlice";
import { getRequests } from "store/thunks/reguests";
import { sendNotification } from "ui/Toast";
import { useDebounce } from "usehooks-ts";

import AdapterDateFns from "@date-io/date-fns";
import DownloadIcon from "@mui/icons-material/Download";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Backdrop,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Modal,
  Pagination,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import AgGrid from "../../features/AgGrid/AgGrid";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { getUserInfo } from "../../store/thunks/authThunk";
import { cancelBtn } from "../../styles/MUIStyles";
import Loader from "../../ui/Loader";

import { LeaveOrder } from "./LeaveOrder";
import { formatDate, readStatus } from "./utils";

import styles from "./styles.module.css";

const RequestStatuses = {
  None: 0,
  AwaitingPM: 1,
  AwaitingHR: 2,
  AwaitingGM: 3,
  Rejected: 4,
  Approved: 5,
  Cancelled: 6,
};

export type Request = {
  createdDate: Date;
  finishDate: Date;
  isStandard: boolean;
  id: number;
  nextApproverName: string;
  pureDays: number;
  remark: string;
  requestStatus: number;
  sign: number;
  startDate: Date;
  employeeName?: string;
  employeeEmail?: string;
  position?: string;
  email?: string;
};

export const Requests = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [isOpenModalApprove, setIsOpenModalApprove] = useState(false);
  const [isOpenModalReject, setIsOpenModalReject] = useState(false);
  const [isOpenModalCancel, setIsOpenModalCancel] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [leaveOrderData, setLeaveOrderData] = useState<Request | null>(null);
  const userRoles = useAppSelector((state) => state.auth.userRoles);

  const allRequests = useAppSelector((state) => state.requests.allRequests);
  const filter = useAppSelector((state) => state.requests.allRequests.params.filter);

  const [nameInput, setNameInput] = useState("");
  const debouncedName = useDebounce(nameInput, 800);

  const handleOpenDownloadModal = (data: Request) => {
    setLeaveOrderData(data);
    setIsOpenModal(true);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    dispatch(setAllRequestsPage(value))
  }

  const handlePageSizeChange = (e: SelectChangeEvent<number>) => {
    const newSize = Number(e.target.value);

    dispatch(setAllRequestsPageSize(newSize));
    dispatch(setAllRequestsPage(1));
    dispatch(getRequests({ pageNumber: 1, pageSize: newSize }));
  };

  const approve = async (id: number) => {
    try {
      setIsLoading(true);
      await axiosApi.post(
        `/personal-cabinet/calendar/daysoff/request/${id}/approve`,
      );
      sendNotification(t("requests.notification.approveSuccess"), "success");
    } catch (e) {
      sendNotification(t("requests.notification.approveError"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const reject = async (id: number) => {
    try {
      setIsLoading(true);
      await axiosApi.post(
        `/personal-cabinet/calendar/daysoff/request/${id}/reject`,
      );
      sendNotification(t("requests.notification.rejectSuccess"), "success");
    } catch (e) {
      sendNotification(t("requests.notification.rejectError"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const cancel = async (id: number) => {
    try {
      setIsLoading(true);
      await axiosApi.post(
        `/personal-cabinet/calendar/daysoff/request/${id}/cancel`,
      );
      sendNotification(t("requests.notification.cancelSuccess"), "success");
    } catch (e) {
      sendNotification(t("requests.notification.cancelError"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const ddMMyyyyToYyyyMMdd = (ddMMyyyy: string): string => {
    if (!ddMMyyyy) return "";
    const parts = ddMMyyyy.split(".");
    if (parts.length !== 3) return "";
    const [dd, mm, yyyy] = parts;
    const d = dd.padStart(2, "0");
    const m = mm.padStart(2, "0");
    const y = yyyy;
    return `${y}-${m}-${d}`;
  };


  useEffect(() => {
    const raw = allRequests.params.filter?.createdDateTime ?? "";
    const preparedFilter = {
      ...allRequests.params.filter,
      createdDateTime: raw
        ? (() => {
          const parts = raw.split(".");
          if (parts.length !== 3) return "";
          const [dd, mm, yyyy] = parts;
          if (!/^\d{1,2}$/.test(dd) || !/^\d{1,2}$/.test(mm) || !/^\d{4}$/.test(yyyy)) return "";
          return ddMMyyyyToYyyyMMdd(raw);
        })()
        : "",
    };
    dispatch(
      getRequests({
        pageNumber: allRequests.params.pageNumber,
        pageSize: allRequests.params.pageSize,
        filter: preparedFilter,
      })
    );
  }, [
    dispatch,
    allRequests.params.pageNumber,
    allRequests.params.pageSize,
    allRequests.params.filter,
  ]);

  useEffect(() => {
    dispatch(
      setAllRequestsFilter({
        ...filter,
        name: debouncedName,
      })
    );

    dispatch(setAllRequestsPage(1));
  }, [debouncedName]);

  useEffect(() => {
    dispatch(getUserInfo());
  }, [dispatch]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleApproveRequest = () => {
    setIsOpenModalApprove(true);
    handleCloseMenu();
  };

  const handleConfirmApprove = async () => {
    if (selectedRow?.id) {
      await approve(selectedRow.id);
      setIsOpenModalApprove(false);
      dispatch(getRequests({ pageNumber: allRequests.params.pageNumber, pageSize: allRequests.params.pageSize }));
    }
  };

  const handleRejectRequest = () => {
    setIsOpenModalReject(true);
    handleCloseMenu();
  };

  const handleConfirmReject = async () => {
    if (selectedRow?.id) {
      await reject(selectedRow.id);
      setIsOpenModalReject(false);
      dispatch(getRequests({ pageNumber: allRequests.params.pageNumber, pageSize: allRequests.params.pageSize }));
    }
  };

  const handleConfirmCancel = async () => {
    if (selectedRow?.id) {
      await cancel(selectedRow.id);
      setIsOpenModalCancel(false);
      dispatch(getRequests({ pageNumber: allRequests.params.pageNumber, pageSize: allRequests.params.pageSize }));
    }
  };

  const columnDefs = [
    {
      headerName: t("requests.column.id"),
      field: "id",
      resizable: true,
      width: 90,
    },
    {
      headerName: t("requests.column.employeeName"),
      field: "employeeName",
      resizable: true,
      width: 220,
    },
    {
      headerName: t("requests.column.email"),
      field: "employeeEmail",
      resizable: true,
      width: 200,
    },
    {
      headerName: t("data.position"),
      field: "position",
      resizable: true,
      width: 150,
    },
    {
      headerName: t("requests.column.remark"),
      field: "remark",
      resizable: true,
      width: 150,
    },
    {
      headerName: t("requests.column.createdDate"),
      field: "createdDate",
      resizable: true,
      width: 150,
      valueGetter: (params: any) => {
        const createdDate = new Date(params.data.createdDate);
        return formatDate(createdDate);
      },
    },
    {
      headerName: t("requests.column.period"),
      resizable: true,
      width: 220,
      valueGetter: (params: any) => {
        const startDate = new Date(params.data.startDate);
        const finishDate = new Date(params.data.finishDate);
        return `${formatDate(startDate)} - ${formatDate(finishDate)}`;
      },
    },
    {
      headerName: t("requests.column.status"),
      field: "requestStatus",
      valueFormatter: (params: any) => readStatus(params.value),
      resizable: true,
      width: 320,
    },
    {
      headerName: t("requests.column.nextApproverName"),
      field: "nextApproverEmail",
      resizable: true,
      width: 200,
      cellRenderer: (params: any) => {
        return (
          <span className={styles.responsibleMenuItem}>
            <span>{params.data.nextApproverName}</span>
            {!!params.data.nextApproverEmail && (
              <span className={styles.responsiblePost}>
                {params.data.nextApproverEmail}
              </span>
            )}
          </span>
        );
      },
    },
    {
      headerName: t("requests.column.isStandard"),
      field: "isStandard",
      resizable: true,
      width: 180,
      cellRenderer: (params: any) => {
        return (
          <span>
            {params.value ? t("requests.standard") : t("requests.nonStandard")}
          </span>
        );
      },
    },
    // {
    //   headerName: "",
    //   field: "actions",
    //   width: 60,
    //   resizable: false,
    //   cellRenderer: (params: any) => {
    //     return (
    //       <IconButton onClick={(event) => handleMenuClick(event, params.data)}>
    //         <MoreVertIcon />
    //       </IconButton>
    //     );
    //   },
    // },
    // {
    //   headerName: "",
    //   field: "actions",
    //   width: 70,
    //   resizable: false,
    //   cellRenderer: (params: any) => {
    //     if (userRole !== "HumanResources") return null;
    //     return (
    //       <Tooltip title={t("buttons.PrintDocument")}>
    //         <IconButton onClick={() => handleOpenDownloadModal(params?.data)}>
    //           <DownloadIcon />
    //         </IconButton>
    //       </Tooltip>
    //     );
    //   },
    // },
    {
      headerName: "Actions",
      field: "actions",
      width: 150,
      resizable: false,
      cellRenderer: (params: any) => {
        return (
          <div style={{ display: "flex", gap: 8 }}>
            <IconButton onClick={(e) => handleMenuClick(e, params.data)}>
              <MoreVertIcon />
            </IconButton>
            {userRoles.includes("HumanResources") && (
              <Tooltip title={t("buttons.PrintDocument")}>
                <IconButton
                  onClick={() => handleOpenDownloadModal(params?.data)}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  const parsedDateValue = (() => {
    const s = filter?.createdDateTime;
    if (!s) return null;
    const parts = s.split(".");
    if (parts.length !== 3) return null;
    const [dd, mm, yyyy] = parts;
    if (!/^\d{1,2}$/.test(dd) || !/^\d{1,2}$/.test(mm) || !/^\d{4}$/.test(yyyy)) return null;
    const d = parseInt(dd, 10);
    const m = parseInt(mm, 10);
    const y = parseInt(yyyy, 10);
    const dt = new Date(y, m - 1, d);
    return isNaN(dt.getTime()) ? null : dt;
  })();

  return (
    <div style={{ position: "relative" }}>
      {allRequests?.response?.loading && (
        <div
          style={{
            position: "absolute",
            zIndex: 1000,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <Loader />
        </div>
      )}
      <div className={styles.header}>
        <h4 style={{ flex: 1 }}>{t("tabs.applications")}</h4>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
            mb: 2,
          }}
        >
          <TextField
            size="small"
            label={t("requests.filterNameLabel")}
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t("requests.filterStatusLabel")}</InputLabel>
            <Select
              value={filter?.status || ""}
              label={t("requests.filterStatusLabel")}
              onChange={(e) =>
                dispatch(
                  setAllRequestsFilter({
                    ...filter,
                    status: e.target.value,
                  })
                )
              }
            >
              <MenuItem value="">
                {t("requests.filterOptionAll")}
              </MenuItem>

              <MenuItem value={RequestStatuses.AwaitingPM.toString()}>
                {t("requests.awaitingPM")}
              </MenuItem>

              <MenuItem value={RequestStatuses.AwaitingGM.toString()}>
                {t("requests.awaitingGM")}
              </MenuItem>

              <MenuItem value={RequestStatuses.Approved.toString()}>
                {t("requests.approved")}
              </MenuItem>

              <MenuItem value={RequestStatuses.Rejected.toString()}>
                {t("requests.rejected")}
              </MenuItem>

              <MenuItem value={RequestStatuses.Cancelled.toString()}>
                {t("requests.cancelled")}
              </MenuItem>
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DesktopDatePicker
              label={t("requests.filterDateLabel")}
              inputFormat="dd.MM.yyyy"
              value={parsedDateValue}
              onChange={(date) => {
                const isDateValid = date instanceof Date && !isNaN(date.getTime());

                if (isDateValid) {
                  const dd = String(date.getDate()).padStart(2, "0");
                  const mm = String(date.getMonth() + 1).padStart(2, "0");
                  const yyyy = date.getFullYear();
                  const formatted = `${dd}.${mm}.${yyyy}`;
                  dispatch(
                    setAllRequestsFilter({
                      ...filter,
                      createdDateTime: formatted,
                    })
                  );
                } else {
                  dispatch(
                    setAllRequestsFilter({
                      ...filter,
                      createdDateTime: "",
                    })
                  );
                }
              }}
              renderInput={(params) => (
                <TextField {...params} size="small" sx={{ minWidth: 200 }} />
              )}
            />
          </LocalizationProvider>

          <Button
            variant="outlined"
            onClick={() => {
              dispatch(
                setAllRequestsFilter({
                  name: "",
                  status: "",
                  createdDateTime: "",
                })
              );
              setNameInput("");
              dispatch(setAllRequestsPage(1));
            }}
          >
            {t("requests.resetFilters")}
          </Button>
        </Box>
      </div>
      <div className={isLoading ? styles.opacity : ""}>
        <AgGrid
          rowData={allRequests?.response?.data?.data}
          columnDefs={columnDefs}
          height="720px"
          activeRow={{}}
        />
      </div>
      <Stack
        spacing={2}
        sx={{
          margin: "30px auto 20px auto",
          width: "500px",
          alignItems: "center",
          "@media (max-width: 515px)": {
            width: "400px",
          },
          "@media (max-width: 420px)": {
            width: "300px",
          },
        }}
      >
        <Pagination
          count={Math.ceil((allRequests?.response?.data?.metadata?.totalItemCount || 0) / allRequests.params.pageSize)}
          page={allRequests.params.pageNumber ?? 1}
          onChange={handlePageChange}
          color="primary"
          size="medium"
          variant="outlined"
          shape="rounded"
          sx={{ margin: "0 auto" }}
        />
        <>
          <Select
            value={allRequests?.params?.pageSize}
            name="pageSize"
            onChange={handlePageSizeChange}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: "12px",
                  padding: "0 10px",
                  "& .MuiMenuItem-root:hover": {
                    backgroundColor: "#e3f2fd",
                  },
                },
              },
            }}
            sx={{
              borderRadius: "12px",
              border: "1px solid #C0D4E3",
              width: "100px",
              margin: "0 auto",
              ".MuiOutlinedInput-notchedOutline": { border: 0 },
              ".MuiSelect-select": {
                padding: "10px 32px 10px 12px",
                color: "#3E444A",
                fontFamily: "Raleway",
                fontSize: "14px",
                lineHeight: "24px",
              },
            }}
          >
            {[10, 20, 30, 40, 50].map((value) => (
              <MenuItem
                key={value}
                value={value}
                sx={{ borderRadius: "10px" }}
              >
                {value}
              </MenuItem>
            ))}
          </Select>
        </>
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleApproveRequest}>
          {t("requests.approveButton")}
        </MenuItem>
        <MenuItem onClick={handleRejectRequest}>
          {t("requests.rejectButton")}
        </MenuItem>
      </Menu>
      <Modal
        open={isOpenModalApprove}
        onClose={() => setIsOpenModalApprove(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t("requests.approveModalTitle", {
              employeeName: selectedRow?.employeeName,
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("requests.approveModalDescription")}
          </Typography>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmApprove}
            sx={{ mr: 2, textTransform: "none" }}
          >
            {t("requests.approveButton")}
          </Button>
          <Button
            sx={{ ...cancelBtn }}
            variant="outlined"
            onClick={() => setIsOpenModalApprove(false)}
          >
            {t("requests.noButton")}
          </Button>
        </Box>
      </Modal>
      <Modal
        open={isOpenModalReject}
        onClose={() => setIsOpenModalReject(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t("requests.rejectModalTitle", {
              employeeName: selectedRow?.employeeName,
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("requests.rejectModalDescription")}
          </Typography>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmReject}
            sx={{ mr: 2, textTransform: "none" }}
          >
            {t("requests.rejectButton")}
          </Button>
          <Button
            sx={{ ...cancelBtn }}
            variant="outlined"
            onClick={() => setIsOpenModalReject(false)}
          >
            {t("requests.noButton")}
          </Button>
        </Box>
      </Modal>
      <Modal
        open={isOpenModalCancel}
        onClose={() => setIsOpenModalCancel(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t("requests.cancelModalTitle", {
              employeeName: selectedRow?.employeeName,
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("requests.cancelModalDescription")}
          </Typography>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmCancel}
            sx={{ mr: 2, textTransform: "none" }}
          >
            {t("requests.cancelButton")}
          </Button>
          <Button
            sx={{ ...cancelBtn }}
            variant="outlined"
            onClick={() => setIsOpenModalCancel(false)}
          >
            {t("requests.noButton")}
          </Button>
        </Box>
      </Modal>
      {leaveOrderData && (
        <Modal
          aria-labelledby="transition-modal-title"
          aria-describedby="transition-modal-description"
          open={isOpenModal}
          onClose={() => setIsOpenModal(false)}
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{
            backdrop: {
              timeout: 500,
            },
          }}
        >
          <LeaveOrder {...leaveOrderData} />
        </Modal>
      )}
    </div>
  );
};
